/**
 * Messaging API + TanStack Query hooks. Backend: `backend/messaging/`.
 *
 * Who may message whom is decided on the server (`messaging/policy.py`), so
 * the contacts list is the source of truth for the "New message" picker.
 * Threads poll for new messages with `?after=<last id>` while open, which
 * keeps each poll to a handful of rows.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { UserRole } from "./auth";
import { api } from "./client";
import { toQueryString, type Paginated } from "./types";

/* ─────────────────────────────────────────── Shapes */

export interface Person {
  id: number;
  full_name: string;
  role: UserRole | "";
  avatar: string | null;
}

export interface Conversation {
  id: number;
  subject: string;
  student: { id: number; full_name: string } | null;
  participants: Person[];
  last_message: { body: string; sender_name: string; is_mine: boolean; has_attachment: boolean } | null;
  last_message_at: string | null;
  unread_count: number;
  /** False for an admin reading someone else's conversation for oversight. */
  is_participant: boolean;
  /** Parents of any student in the conversation are copied in. */
  parent_copy: boolean;
  created_at: string;
}

export type RoleFilter = "parent" | "student" | "tutor" | "admin";

export interface FlaggedMessage {
  id: number;
  sender: Person | null;
  conversation: { id: number; subject: string } | null;
  recipients: string;
  subject: string;
  body: string;
  reason: string;
  detected: string[];
  layer: "pattern" | "ai";
  reviewed: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface ChatMessage {
  id: number;
  sender: Person | null;
  is_mine: boolean;
  body: string;
  attachment: { url: string; name: string; size: number } | null;
  created_at: string;
}

export interface Contact {
  id: number;
  full_name: string;
  role: UserRole | "";
  avatar: string | null;
  /** "Parent of Sam", "Tutor", "DrTutor team". */
  context: string;
}

export interface NewConversationInput {
  recipients: number[];
  subject: string;
  body: string;
  student?: number | null;
  attachment?: File | null;
  parent_copy?: boolean;
}

/* ─────────────────────────────────────────── Helpers */

function asForm(fields: Record<string, string | number | null | undefined | File | number[]>): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach((item) => form.append(k, String(item)));
    else if (v instanceof File) form.append(k, v, v.name);
    else form.append(k, String(v));
  }
  return form;
}

/* ─────────────────────────────────────────── Queries */

const KEY = ["messages"] as const;

export function useConversations(
  opts: { everyone?: boolean; search?: string; role?: RoleFilter } = {},
): UseQueryResult<Paginated<Conversation>> {
  return useQuery({
    queryKey: [...KEY, "conversations", opts],
    queryFn: () =>
      api
        .get<Paginated<Conversation>>(
          `/messages/conversations/${toQueryString({
            page_size: 50,
            scope: opts.everyone ? "everyone" : undefined,
            search: opts.search || undefined,
            role: opts.role,
          })}`,
        )
        .then((r) => r.data),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    // Search and scope changes keep the current list until the new one lands.
    placeholderData: keepPreviousData,
  });
}

export function useConversation(id: number | null): UseQueryResult<Conversation> {
  return useQuery({
    queryKey: [...KEY, "conversation", id],
    queryFn: () => api.get<Conversation>(`/messages/conversations/${id}/`).then((r) => r.data),
    enabled: id != null,
  });
}

interface ThreadPage {
  results: ChatMessage[];
  has_more: boolean;
}

/** The thread, newest page first in `pages`; `fetchNextPage` loads older. */
export function useThread(id: number | null): UseInfiniteQueryResult<InfiniteData<ThreadPage>> {
  return useInfiniteQuery({
    queryKey: [...KEY, "thread", id],
    queryFn: ({ pageParam }) =>
      api
        .get<ThreadPage>(`/messages/conversations/${id}/messages/${toQueryString({ before: pageParam ?? undefined })}`)
        .then((r) => r.data),
    initialPageParam: null as number | null,
    getNextPageParam: (page) => (page.has_more && page.results.length > 0 ? page.results[0].id : undefined),
    enabled: id != null,
    staleTime: Infinity,
  });
}

/** Poll for messages newer than the newest one on screen and splice them
 *  into the thread cache. Returns nothing; the thread query re-renders. */
export function useThreadPolling(id: number | null, newestId: number | null, intervalMs = 8_000): void {
  const qc = useQueryClient();
  useQuery({
    queryKey: [...KEY, "thread-poll", id, newestId],
    queryFn: async () => {
      const fresh = await api
        .get<ThreadPage>(`/messages/conversations/${id}/messages/${toQueryString({ after: newestId ?? 0 })}`)
        .then((r) => r.data.results);
      if (fresh.length > 0) {
        qc.setQueryData<InfiniteData<ThreadPage>>([...KEY, "thread", id], (data) => appendMessages(data, fresh));
        void qc.invalidateQueries({ queryKey: [...KEY, "conversations"] });
      }
      return fresh.length;
    },
    enabled: id != null && newestId != null,
    refetchInterval: intervalMs,
    refetchOnWindowFocus: true,
  });
}

function appendMessages(data: InfiniteData<ThreadPage> | undefined, fresh: ChatMessage[]): InfiniteData<ThreadPage> | undefined {
  if (!data || data.pages.length === 0) return data;
  const seen = new Set(data.pages.flatMap((p) => p.results.map((m) => m.id)));
  const add = fresh.filter((m) => !seen.has(m.id));
  if (add.length === 0) return data;
  const [newest, ...older] = data.pages;
  return { ...data, pages: [{ ...newest, results: [...newest.results, ...add] }, ...older] };
}

export function useContacts(enabled = true): UseQueryResult<Contact[]> {
  return useQuery({
    queryKey: [...KEY, "contacts"],
    queryFn: () => api.get<{ results: Contact[] }>("/messages/contacts/").then((r) => r.data.results),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useMessageStudents(enabled = true): UseQueryResult<Array<{ id: number; full_name: string }>> {
  return useQuery({
    queryKey: [...KEY, "students"],
    queryFn: () =>
      api.get<{ results: Array<{ id: number; full_name: string }> }>("/messages/students/").then((r) => r.data.results),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Conversations with unread messages, for the nav badge. */
export function useMessagesUnread(enabled = true): UseQueryResult<number> {
  return useQuery({
    queryKey: [...KEY, "unread-count"],
    queryFn: () => api.get<{ count: number }>("/messages/unread-count/").then((r) => r.data.count),
    enabled,
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/* ─────────────────────────────────────────── Mutations */

export function useStartConversation(): UseMutationResult<Conversation, unknown, NewConversationInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => {
      const body = input.attachment
        ? asForm({
            recipients: input.recipients,
            subject: input.subject,
            body: input.body,
            student: input.student ?? undefined,
            attachment: input.attachment,
            parent_copy: input.parent_copy === false ? "false" : undefined,
          })
        : {
            recipients: input.recipients,
            subject: input.subject,
            body: input.body,
            student: input.student ?? null,
            parent_copy: input.parent_copy ?? true,
          };
      return api.post<Conversation>("/messages/conversations/", body).then((r) => r.data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, "conversations"] });
    },
  });
}

export function useSendMessage(
  id: number | null,
): UseMutationResult<ChatMessage, unknown, { body: string; attachment?: File | null }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, attachment }) => {
      const payload = attachment ? asForm({ body, attachment }) : { body };
      return api.post<ChatMessage>(`/messages/conversations/${id}/messages/`, payload).then((r) => r.data);
    },
    onSuccess: (message) => {
      qc.setQueryData<InfiniteData<ThreadPage>>([...KEY, "thread", id], (data) => appendMessages(data, [message]));
      void qc.invalidateQueries({ queryKey: [...KEY, "conversations"] });
      void qc.invalidateQueries({ queryKey: [...KEY, "unread-count"] });
    },
  });
}

export function useMarkConversationRead(): UseMutationResult<unknown, unknown, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/messages/conversations/${id}/read/`),
    onMutate: (id) => {
      qc.setQueriesData<Paginated<Conversation>>({ queryKey: [...KEY, "conversations"] }, (data) =>
        data ? { ...data, results: data.results.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)) } : data,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, "unread-count"] });
      // Reading a conversation also clears its message notifications.
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useSetParentCopy(id: number): UseMutationResult<Conversation, unknown, boolean> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled) =>
      api.post<Conversation>(`/messages/conversations/${id}/parent-copy/`, { enabled }).then((r) => r.data),
    onSuccess: (fresh) => {
      qc.setQueryData([...KEY, "conversation", id], fresh);
      qc.setQueriesData<Paginated<Conversation>>({ queryKey: [...KEY, "conversations"] }, (data) =>
        data ? { ...data, results: data.results.map((c) => (c.id === id ? fresh : c)) } : data,
      );
    },
  });
}

/* ─────────────────────────────────────────── Flagged (admin) */

export function useFlaggedMessages(
  reviewed: boolean,
  enabled = true,
): UseQueryResult<Paginated<FlaggedMessage>> {
  return useQuery({
    queryKey: [...KEY, "flagged", reviewed],
    queryFn: () =>
      api
        .get<Paginated<FlaggedMessage>>(`/messages/flagged/${toQueryString({ reviewed: String(reviewed), page_size: 50 })}`)
        .then((r) => r.data),
    enabled,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useReviewFlagged(): UseMutationResult<FlaggedMessage, unknown, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post<FlaggedMessage>(`/messages/flagged/${id}/review/`).then((r) => r.data),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, "flagged"] });
    },
  });
}
